import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTicket, useAssignTicket } from '@/hooks/useTickets';
import { DashboardLayout } from '@/components/shared/DashboardLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { TicketFlowStepper } from '@/components/shared/TicketFlowStepper';
import { StatusBadge, PriorityBadge } from '@/components/shared/StatusBadge';
import { SlaTimer } from '@/components/shared/SlaTimer';
import IssueDescription from '@/components/shared/IssueDescription';
import { toast } from 'sonner';
import { usersApi } from '@/lib/api';
import { ArrowLeft, MapPin, Star, User, Phone, MagnifyingGlass } from '@phosphor-icons/react';

export const AssignTechnician = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [selectedTech, setSelectedTech] = useState(null);
  const [technicians, setTechnicians] = useState([]);
  const [loadingTechs, setLoadingTechs] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  const { data: ticket, isLoading } = useTicket(id);
  const assignTicket = useAssignTicket();

  // Fetch real technicians from API
  useEffect(() => {
    const fetchTechnicians = async () => {
      try {
        const { data } = await usersApi.list({ role: 'technician' });
        setTechnicians(data || []);
      } catch (error) {
        toast.error('Failed to load technicians');
      } finally {
        setLoadingTechs(false);
      }
    };
    fetchTechnicians();
  }, []);

  const filteredTechs = technicians.filter(t =>
    t.is_active &&
    (t.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
     t.phone?.includes(searchQuery))
  );

  const handleAssign = async () => {
    if (!selectedTech) {
      toast.error('Please select a technician');
      return;
    }

    try {
      await assignTicket.mutateAsync({ id, techId: selectedTech.id });
      toast.success(`Ticket assigned to ${selectedTech.full_name}`);
      navigate('/ops/foc');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign ticket');
    }
  };

  if (isLoading) {
    return (
      <DashboardLayout role="foc">
        <div className="p-8 max-w-5xl mx-auto">
          <div className="animate-pulse space-y-4">
            <div className="h-8 w-48 bg-slate-200 rounded" />
            <div className="h-64 bg-slate-100 rounded-lg" />
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!ticket) {
    return (
      <DashboardLayout role="foc">
        <div className="p-8 text-center">
          <p className="text-muted-foreground">Ticket not found</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout role="foc">
      <div className="p-6 lg:p-8 max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/ops/foc')}
            className="mb-4"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back to Queue
          </Button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-black tracking-tight mb-1">
                Assign Technician
              </h1>
              <div className="flex items-center gap-3">
                <span className="font-bold text-lg">{ticket.ticket_number}</span>
                <PriorityBadge priority={ticket.priority} />
                <StatusBadge status={ticket.status} />
              </div>
            </div>
            <SlaTimer deadline={ticket.sla_deadline} status={ticket.status} />
          </div>
        </div>

        {/* Flow Stepper */}
        <Card className="p-4 mb-6">
          <TicketFlowStepper ticket={ticket} />
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Ticket Info */}
          <div className="space-y-4">
            <Card className="p-4 bg-blue-50 border-blue-200">
              <h3 className="font-bold mb-3 text-sm">Ticket Details</h3>
              <div className="space-y-2 text-sm">
                <p><span className="font-semibold">Customer:</span> {ticket.customer_name}</p>
                <p><span className="font-semibold">Product:</span> {ticket.product_name}</p>
                <p><span className="font-semibold">Issue:</span> {ticket.issue_category}</p>
                <p><span className="font-semibold">Priority:</span> {ticket.priority}</p>
                {ticket.service_address && (
                  <div className="flex items-start gap-1 mt-2 p-2 bg-white rounded">
                    <MapPin size={14} className="mt-0.5 text-blue-500 shrink-0" />
                    <span className="text-xs">{ticket.service_address}</span>
                  </div>
                )}
              </div>
            </Card>

            <IssueDescription description={ticket.description} />
          </div>

          {/* Technician Selection */}
          <div className="lg:col-span-2 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-lg">Available Technicians</h3>
              <span className="text-sm text-muted-foreground">{filteredTechs.length} found</span>
            </div>

            {/* Search */}
            <div className="relative">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search technicians..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* Technician List */}
            {loadingTechs ? (
              <div className="space-y-2">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="h-16 bg-slate-100 rounded-lg animate-pulse" />
                ))}
              </div>
            ) : (
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {filteredTechs.map((tech) => (
                  <Card 
                    key={tech.id}
                    className={`p-4 cursor-pointer transition-all ${
                      selectedTech?.id === tech.id 
                        ? 'border-primary border-2 bg-primary/5 ring-1 ring-primary/20' 
                        : 'hover:bg-muted/30'
                    }`}
                    onClick={() => setSelectedTech(tech)}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-emerald-500 text-white flex items-center justify-center font-bold text-sm shrink-0">
                        {tech.full_name?.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm">{tech.full_name}</p>
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <Phone size={11} /> {tech.phone}
                        </p>
                      </div>
                      {selectedTech?.id === tech.id && (
                        <span className="text-xs font-bold text-primary bg-primary/10 px-2 py-1 rounded">
                          Selected
                        </span>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            )}

            <Button 
              onClick={handleAssign}
              disabled={!selectedTech || assignTicket.isPending}
              className="w-full font-bold"
              size="lg"
            >
              {assignTicket.isPending 
                ? 'Assigning...' 
                : selectedTech 
                  ? `Assign to ${selectedTech.full_name}` 
                  : 'Select a technician'}
            </Button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};